"use client";

import { FC } from "react";
import { useParams } from "next/navigation";
import { Icon } from "@ospweb/sdk-ui";
import Translations from "@translate/components/Member/Subscription/CrossChannelGuideModal/CrossChannelGuideModalContent";
import { I18nUtil } from "@utils/i18n";
import { ROUTER_PATH } from "@config/router";
import { useIsPc } from "@hooks/useIsPc";
import { ShellLink } from "@components/ShellNavigation/ShellAwareLink";

const I18n = I18nUtil.createScoped(Translations);

/** manage：去订阅管理关闭原订阅的自动续费；dismissed：关闭弹窗 */
export type CrossChannelGuideResult = "manage" | "dismissed";

export interface CrossChannelGuideModalContentProps {
  onDone: (result: CrossChannelGuideResult) => void;
}

const PRIMARY_BUTTON_CLASS = "button primary_button button_m w-full";
const SECONDARY_BUTTON_CLASS = "button secondary_button button_m w-full";

/**
 * 下单被后端跨渠道互斥拦截（146087）时的引导（EDG-552）：说明原因，并带用户去订阅管理。
 *
 * 只做引导、不代用户关闭自动续费（需求方决定）：关闭续费是用户自己的决定，由订阅管理里的现有流程完成
 * （含挽留与确认）。入口与 `ManageBillingUpgrade` 一致 —— PC 打开账单管理弹窗（调用方处理 `manage`），
 * H5 跳订阅管理页。
 */
export const CrossChannelGuideModalContent: FC<
  CrossChannelGuideModalContentProps
> = ({ onDone }) => {
  const [isPc] = useIsPc();
  const { locale } = useParams<{ locale: string }>();

  return (
    <div className="relative flex flex-col gap-4 p-4 md:p-6">
      <button
        type="button"
        data-openreplay-label="member_crosschannelguide_close"
        aria-label={I18n.t("Close")}
        className="absolute top-3 right-3 z-10 flex items-center justify-center"
        onClick={() => onDone("dismissed")}
      >
        <Icon
          name="close"
          size={24}
          className="text-content_primary cursor-pointer button_icon"
        />
      </button>
      <div className="text-content_primary headline_regular_s md:headline_regular_m pr-8">
        {I18n.t("You already have an active subscription")}
      </div>
      <p className="body_m text-content_primary">
        {I18n.t("Your current subscription uses a different payment method.")}{" "}
        {I18n.t(
          "To pay with this method, first turn off auto-renew on your current subscription. You'll keep your benefits until the end of the current period.",
        )}
      </p>
      <div className="flex flex-col gap-2">
        {isPc ? (
          <button
            type="button"
            data-openreplay-label="member_crosschannelguide_manage"
            className={PRIMARY_BUTTON_CLASS}
            onClick={() => onDone("manage")}
          >
            {I18n.t("Manage subscription")}
          </button>
        ) : (
          <ShellLink
            prefetch
            href={`/${locale}${ROUTER_PATH.MEMBER_SUBSCRIPTION}`}
            data-openreplay-label="member_crosschannelguide_manage"
            className={PRIMARY_BUTTON_CLASS}
            onClick={() => onDone("dismissed")}
          >
            {I18n.t("Manage subscription")}
          </ShellLink>
        )}
        <button
          type="button"
          data-openreplay-label="member_crosschannelguide_dismiss"
          className={SECONDARY_BUTTON_CLASS}
          onClick={() => onDone("dismissed")}
        >
          {I18n.t("Not now")}
        </button>
      </div>
    </div>
  );
};
